import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  adminGetComboSalary,
  adminGetComboBenefit,
  adminGetComboInsurance,
  adminGetComboDeduction,
  adminGetComboMember,
  adminCreatePayslip,
} from "helpers/backend_helpers";
import { toast } from "react-toastify";
import SearchableSelect from "Components/Common/SearchableSelect";
import BreadCrumb from "Components/Common/BreadCrumb";
import "flatpickr/dist/flatpickr.css";
import CurrencyInput from "Components/CurrencyInput";
import { formatCurrency } from "helpers/globalFunction";

interface BenefitItem {
  benefitId: string;
  benefitName: string;
  benefitValue: number;
  useCombo: boolean;
}

interface InsuranceItem {
  insuranceId: string;
  insuranceName: string;
  insuranceValue: number;
  useCombo: boolean;
}

interface DeductionItem {
  deductionId: string;
  deductionName: string;
  deductionValue: number;
  useCombo: boolean;
}

interface PayslipFormValues {
  reg: string;
  salaryId: string;
  salaryAmount: number;
  overtime: number;
  overtimeBackup: number;
  overtimeHours: number;
  workDay: number;
  userId: string;
  userDetails: any;
  payDate: Date;
  note: string;
  isPublish: boolean;
}

const AddPayslip = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [salaryOptions, setSalaryOptions] = useState<any[]>([]);
  const [benefitOptions, setBenefitOptions] = useState<any[]>([]);
  const [insuranceOptions, setInsuranceOptions] = useState<any[]>([]);
  const [deductionOptions, setDeductionOptions] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState<any>(null);

  const [benefits, setBenefits] = useState<BenefitItem[]>([]);
  const [insurances, setInsurances] = useState<InsuranceItem[]>([]);
  const [deductions, setDeductions] = useState<DeductionItem[]>([]);

  const [useSalaryCombo, setUseSalaryCombo] = useState(true);

  // Fetch combo data
  useEffect(() => {
    fetchComboData();
  }, []);

  const fetchComboData = async () => {
    try {
      const [salaries, benefitsData, insurancesData, deductionsData] =
        await Promise.all([
          adminGetComboSalary(),
          adminGetComboBenefit(),
          adminGetComboInsurance(),
          adminGetComboDeduction(),
        ]);

      setSalaryOptions(
        (salaries.data || salaries).map((item: any) => ({
          label: `${item.name || item.label} - ${formatCurrency(item.salary || item.value)}`,
          value: item._id || item.id,
          salary: item.salary || item.value,
        })),
      );

      setBenefitOptions(
        (benefitsData.data || benefitsData).map((item: any) => ({
          label: item.name || item.label,
          value: item._id || item.id,
          amount: item.amount || item.value || 0,
        })),
      );

      setInsuranceOptions(
        (insurancesData.data || insurancesData).map((item: any) => ({
          label: item.name || item.label,
          value: item._id || item.id,
          amount: item.amount || item.value || 0,
        })),
      );

      setDeductionOptions(
        (deductionsData.data || deductionsData).map((item: any) => ({
          label: item.name || item.label,
          value: item._id || item.id,
          amount: item.amount || item.value || 0,
        })),
      );
    } catch (error) {
      toast.error("Gagal mengambil data combo");
    }
  };

  const validationSchema = Yup.object({
    reg: Yup.string().required("Nomor registrasi wajib diisi"),
    userId: Yup.string().required("Karyawan wajib dipilih"),
    salaryAmount: Yup.number()
      .min(0, "Nominal gaji tidak boleh negatif")
      .required("Nominal gaji wajib diisi"),
    overtime: Yup.number().min(0, "Uang lembur tidak boleh negatif"),
    overtimeBackup: Yup.number().min(
      0,
      "Uang lembur cadangan tidak boleh negatif",
    ),
    overtimeHours: Yup.number().min(0, "Jam lembur tidak boleh negatif"),
    workDay: Yup.number()
      .min(0, "Hari kerja tidak boleh negatif")
      .max(31, "Hari kerja maksimal 31 hari")
      .required("Hari kerja wajib diisi"),
    payDate: Yup.date().required("Tanggal slip gaji wajib diisi"),
  });

  const formik = useFormik<PayslipFormValues>({
    initialValues: {
      reg: "",
      salaryId: "",
      salaryAmount: 0,
      overtime: 0,
      overtimeBackup: 0,
      overtimeHours: 0,
      workDay: 0,
      userId: "",
      userDetails: {},
      payDate: new Date(),
      note: "",
      isPublish: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Format benefits, insurances, and deductions based on useCombo flag
        const formattedBenefits = benefits.map((b) =>
          b.useCombo
            ? {
                benefitId: b.benefitId,
                benefitName: b.benefitName,
                benefitValue: b.benefitValue,
              }
            : {
                benefitId: "",
                benefitName: "",
                benefitValue: b.benefitValue,
              },
        );

        const formattedInsurances = insurances.map((i) =>
          i.useCombo
            ? {
                insuranceId: i.insuranceId,
                insuranceName: i.insuranceName,
                insuranceValue: i.insuranceValue,
              }
            : {
                insuranceId: "",
                insuranceName: "",
                insuranceValue: i.insuranceValue,
              },
        );

        const formattedDeductions = deductions.map((d) =>
          d.useCombo
            ? {
                deductionId: d.deductionId,
                deductionName: d.deductionName,
                deductionValue: d.deductionValue,
              }
            : {
                deductionId: "",
                deductionName: "",
                deductionValue: d.deductionValue,
              },
        );

        const payload = {
          ...values,
          benefits: formattedBenefits,
          insurances: formattedInsurances,
          deductions: formattedDeductions,
        };

        await adminCreatePayslip(payload);
        toast.success("Slip gaji berhasil ditambahkan");
        navigate("/slip-gaji");
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "Gagal menambahkan slip gaji",
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleSalaryChange = (selectedOption: any) => {
    if (selectedOption) {
      formik.setFieldValue("salaryId", selectedOption.value);
      formik.setFieldValue("salaryAmount", selectedOption.salary);
    } else {
      formik.setFieldValue("salaryId", "");
      formik.setFieldValue("salaryAmount", 0);
    }
  };

  const handleUserChange = (selectedOption: any) => {
    if (selectedOption) {
      setSelectedMember(selectedOption);
      formik.setFieldValue("userId", selectedOption.value);
      formik.setFieldValue(
        "userDetails",
        selectedOption.details || selectedOption,
      );
    } else {
      setSelectedMember(null);
      formik.setFieldValue("userId", "");
      formik.setFieldValue("userDetails", {});
    }
  };

  const handleMemberSearch = async (searchTerm: string) => {
    try {
      const response = await adminGetComboMember({ name: searchTerm });
      const members = response.data || response;

      return Array.isArray(members) ? members : [];
    } catch (error) {
      console.error("Error searching members:", error);
      return [];
    }
  };

  const formatMemberOption = (item: any) => ({
    label: item.name || item.label || "-",
    value: item._id || item.id,
    details: item,
  });

  // Benefit handlers
  const addBenefit = () => {
    setBenefits([
      ...benefits,
      { benefitId: "", benefitName: "", benefitValue: 0, useCombo: true },
    ]);
  };

  const removeBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const handleBenefitChange = (index: number, selectedOption: any) => {
    const newBenefits = [...benefits];
    if (selectedOption) {
      newBenefits[index] = {
        benefitId: selectedOption.value,
        benefitName: selectedOption.label,
        benefitValue: selectedOption.amount,
        useCombo: true,
      };
    } else {
      newBenefits[index] = {
        benefitId: "",
        benefitName: "",
        benefitValue: 0,
        useCombo: true,
      };
    }
    setBenefits(newBenefits);
  };

  const handleBenefitValueChange = (index: number, value: number) => {
    const newBenefits = [...benefits];
    newBenefits[index].benefitValue = value;
    setBenefits(newBenefits);
  };

  const handleBenefitNameChange = (index: number, name: string) => {
    const newBenefits = [...benefits];
    newBenefits[index].benefitName = name;
    setBenefits(newBenefits);
  };

  const toggleBenefitCombo = (index: number, useCombo: boolean) => {
    const newBenefits = [...benefits];
    newBenefits[index].useCombo = useCombo;
    if (!useCombo) {
      newBenefits[index].benefitId = "";
      newBenefits[index].benefitName = "";
      newBenefits[index].benefitValue = 0;
    } else {
      newBenefits[index].benefitId = "";
      newBenefits[index].benefitName = "";
      newBenefits[index].benefitValue = 0;
    }
    setBenefits(newBenefits);
  };

  // Insurance handlers
  const addInsurance = () => {
    setInsurances([
      ...insurances,
      { insuranceId: "", insuranceName: "", insuranceValue: 0, useCombo: true },
    ]);
  };

  const removeInsurance = (index: number) => {
    setInsurances(insurances.filter((_, i) => i !== index));
  };

  const handleInsuranceChange = (index: number, selectedOption: any) => {
    const newInsurances = [...insurances];
    if (selectedOption) {
      newInsurances[index] = {
        insuranceId: selectedOption.value,
        insuranceName: selectedOption.label,
        insuranceValue: selectedOption.amount,
        useCombo: true,
      };
    } else {
      newInsurances[index] = {
        insuranceId: "",
        insuranceName: "",
        insuranceValue: 0,
        useCombo: true,
      };
    }
    setInsurances(newInsurances);
  };

  const handleInsuranceValueChange = (index: number, value: number) => {
    const newInsurances = [...insurances];
    newInsurances[index].insuranceValue = value;
    setInsurances(newInsurances);
  };

  const handleInsuranceNameChange = (index: number, name: string) => {
    const newInsurances = [...insurances];
    newInsurances[index].insuranceName = name;
    setInsurances(newInsurances);
  };

  const toggleInsuranceCombo = (index: number, useCombo: boolean) => {
    const newInsurances = [...insurances];
    newInsurances[index].useCombo = useCombo;
    if (!useCombo) {
      newInsurances[index].insuranceId = "";
      newInsurances[index].insuranceName = "";
      newInsurances[index].insuranceValue = 0;
    } else {
      newInsurances[index].insuranceId = "";
      newInsurances[index].insuranceName = "";
      newInsurances[index].insuranceValue = 0;
    }
    setInsurances(newInsurances);
  };

  // Deduction handlers
  const addDeduction = () => {
    setDeductions([
      ...deductions,
      { deductionId: "", deductionName: "", deductionValue: 0, useCombo: true },
    ]);
  };

  const removeDeduction = (index: number) => {
    setDeductions(deductions.filter((_, i) => i !== index));
  };

  const handleDeductionChange = (index: number, selectedOption: any) => {
    const newDeductions = [...deductions];
    if (selectedOption) {
      newDeductions[index] = {
        deductionId: selectedOption.value,
        deductionName: selectedOption.label,
        deductionValue: selectedOption.amount,
        useCombo: true,
      };
    } else {
      newDeductions[index] = {
        deductionId: "",
        deductionName: "",
        deductionValue: 0,
        useCombo: true,
      };
    }
    setDeductions(newDeductions);
  };

  const handleDeductionValueChange = (index: number, value: number) => {
    const newDeductions = [...deductions];
    newDeductions[index].deductionValue = value;
    setDeductions(newDeductions);
  };

  const handleDeductionNameChange = (index: number, name: string) => {
    const newDeductions = [...deductions];
    newDeductions[index].deductionName = name;
    setDeductions(newDeductions);
  };

  const toggleDeductionCombo = (index: number, useCombo: boolean) => {
    const newDeductions = [...deductions];
    newDeductions[index].useCombo = useCombo;
    if (!useCombo) {
      newDeductions[index].deductionId = "";
      newDeductions[index].deductionName = "";
      newDeductions[index].deductionValue = 0;
    } else {
      newDeductions[index].deductionId = "";
      newDeductions[index].deductionName = "";
      newDeductions[index].deductionValue = 0;
    }
    setDeductions(newDeductions);
  };

  // Calculate totals
  const calculateTotalIncome = () => {
    const salaryAmount = Number(formik.values.salaryAmount) || 0;
    const benefitsTotal = benefits.reduce(
      (sum, item) => sum + (Number(item.benefitValue) || 0),
      0,
    );
    const overtime = Number(formik.values.overtime) || 0;
    const overtimeBackup = Number(formik.values.overtimeBackup) || 0;
    return salaryAmount + benefitsTotal + overtime + overtimeBackup;
  };

  const calculateTotalDeductions = () => {
    const insurancesTotal = insurances.reduce(
      (sum, item) => sum + (Number(item.insuranceValue) || 0),
      0,
    );
    const deductionsTotal = deductions.reduce(
      (sum, item) => sum + (Number(item.deductionValue) || 0),
      0,
    );
    return insurancesTotal + deductionsTotal;
  };

  const calculateNetSalary = () => {
    return calculateTotalIncome() - calculateTotalDeductions();
  };

  const handleCancel = () => {
    navigate("/slip-gaji");
  };

  document.title = "Tambah Slip Gaji";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tambah Slip Gaji" pageTitle="Gaji" />

          <Form onSubmit={formik.handleSubmit}>
            {/* Basic Information */}
            <Card className="mb-3">
              <CardHeader>
                <h5 className="mb-0">Informasi Dasar</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="reg">
                        Nomor Registrasi <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="reg"
                        name="reg"
                        placeholder="Masukkan nomor registrasi"
                        value={formik.values.reg}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={!!(formik.touched.reg && formik.errors.reg)}
                      />
                      {formik.touched.reg && formik.errors.reg && (
                        <FormFeedback>{formik.errors.reg}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="userId">
                        Karyawan <span className="text-danger">*</span>
                      </Label>
                      <SearchableSelect
                        value={selectedMember}
                        onChange={handleUserChange}
                        onSearch={handleMemberSearch}
                        formatOptionLabel={formatMemberOption}
                        placeholder="Ketik minimal 2 huruf untuk mencari"
                        minSearchLength={2}
                        itemsPerPage={10}
                        isClearable
                        className={
                          formik.touched.userId && formik.errors.userId
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {formik.touched.userId && formik.errors.userId && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.userId}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="payDate">
                        Tanggal Slip Gaji <span className="text-danger">*</span>
                      </Label>
                      <Flatpickr
                        id="payDate"
                        className={`form-control ${
                          formik.touched.payDate && formik.errors.payDate
                            ? "is-invalid"
                            : ""
                        }`}
                        value={formik.values.payDate}
                        onChange={(date: Date[]) =>
                          formik.setFieldValue("payDate", date[0])
                        }
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                      />
                      {formik.touched.payDate && formik.errors.payDate && (
                        <div className="invalid-feedback d-block">
                          {String(formik.errors.payDate)}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="workDay">
                        Hari Kerja <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="workDay"
                        name="workDay"
                        type="number"
                        placeholder="Jumlah hari kerja"
                        value={formik.values.workDay}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          !!(formik.touched.workDay && formik.errors.workDay)
                        }
                      />
                      {formik.touched.workDay && formik.errors.workDay && (
                        <FormFeedback>{formik.errors.workDay}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="note">Catatan</Label>
                      <Input
                        id="note"
                        name="note"
                        type="textarea"
                        rows={3}
                        placeholder="Masukkan catatan tambahan (opsional)"
                        value={formik.values.note}
                        onChange={formik.handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6} lg={3}>
                    <h6 className="mb-1 pb-2">Terbitkan Slip Gaji</h6>
                    <FormGroup>
                      <div className="form-check form-switch mb-3">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="isPublish"
                          name="isPublish"
                          checked={formik.values.isPublish}
                          onChange={(e) => {
                            formik.setFieldValue("isPublish", e.target.checked);
                          }}
                        />
                        <Label className="form-check-label">
                          {formik.values.isPublish ? "Ya" : "Tidak"}
                        </Label>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Row>
              {/* PENDAPATAN */}
              <Col lg={6}>
                <Card className="mb-3">
                  <CardHeader className="bg-soft-success">
                    <h5 className="mb-0 text-success">
                      <i className="ri-money-dollar-circle-line me-2"></i>
                      Pendapatan
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {/* Gaji */}
                    <div className="mb-4">
                      <h6 className="text-muted mb-3 border-bottom pb-2">
                        Gaji Pokok
                      </h6>
                      <FormGroup>
                        <div className="form-check form-switch mb-3">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            checked={useSalaryCombo}
                            onChange={(e) => {
                              setUseSalaryCombo(e.target.checked);
                              if (!e.target.checked) {
                                formik.setFieldValue("salaryId", "");
                              } else {
                                formik.setFieldValue("salaryAmount", 0);
                              }
                            }}
                          />
                          <Label className="form-check-label">
                            Pilih dari data gaji
                          </Label>
                        </div>
                        {useSalaryCombo ? (
                          <Select
                            options={salaryOptions}
                            onChange={handleSalaryChange}
                            isClearable
                            placeholder="Pilih gaji"
                          />
                        ) : (
                          <CurrencyInput
                            id="salaryAmount"
                            name="salaryAmount"
                            placeholder="Masukkan nominal gaji"
                            value={formik.values.salaryAmount}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            required={true}
                            invalid={
                              !!(
                                formik.touched.salaryAmount &&
                                formik.errors.salaryAmount
                              )
                            }
                            error={formik.errors.salaryAmount}
                          />
                        )}
                        {formik.touched.salaryAmount &&
                          formik.errors.salaryAmount && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.salaryAmount as string}
                            </div>
                          )}
                      </FormGroup>
                      <div className="text-end">
                        <small className="text-muted">
                          Total:{" "}
                          <strong>
                            {formatCurrency(formik.values.salaryAmount)}
                          </strong>
                        </small>
                      </div>
                    </div>

                    {/* Tunjangan */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h6 className="text-muted mb-0">Tunjangan</h6>
                        <Button color="success" size="sm" onClick={addBenefit}>
                          <i className="ri-add-line"></i> Tambah
                        </Button>
                      </div>
                      {benefits.map((benefit, index) => (
                        <div
                          key={index}
                          className="border rounded p-3 mb-3 bg-light"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <small className="text-muted fw-bold">
                              Tunjangan #{index + 1}
                            </small>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeBenefit(index)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              checked={benefit.useCombo}
                              onChange={(e) =>
                                toggleBenefitCombo(index, e.target.checked)
                              }
                            />
                            <Label className="form-check-label">
                              Pilih dari data tunjangan
                            </Label>
                          </div>
                          {benefit.useCombo ? (
                            <Select
                              options={benefitOptions}
                              onChange={(option: any) =>
                                handleBenefitChange(index, option)
                              }
                              isClearable
                              placeholder="Pilih tunjangan"
                              className="mb-2"
                            />
                          ) : (
                            <Input
                              type="text"
                              placeholder="Nama tunjangan"
                              value={benefit.benefitName}
                              onChange={(e) =>
                                handleBenefitNameChange(index, e.target.value)
                              }
                              className="mb-2"
                            />
                          )}
                          <CurrencyInput
                            id="benefitValue"
                            name="benefitValue"
                            placeholder="Nominal tunjangan"
                            value={benefit.benefitValue}
                            onChange={(e) =>
                              handleBenefitValueChange(index, e.target.value)
                            }
                          />
                        </div>
                      ))}
                      {benefits.length > 0 && (
                        <div className="text-end">
                          <small className="text-muted">
                            Total Tunjangan:{" "}
                            <strong>
                              {formatCurrency(
                                benefits.reduce(
                                  (sum, b) => sum + Number(b.benefitValue || 0),
                                  0,
                                ),
                              )}
                            </strong>
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Lembur */}
                    <div className="mb-3">
                      <h6 className="text-muted mb-3 border-bottom pb-2">
                        Lembur
                      </h6>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label
                              for="overtimeHours"
                              className="form-label-sm"
                            >
                              Jam Lembur
                            </Label>
                            <Input
                              id="overtimeHours"
                              name="overtimeHours"
                              type="number"
                              placeholder="Jam lembur"
                              value={formik.values.overtimeHours}
                              onChange={formik.handleChange}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="overtime" className="form-label-sm">
                              Uang Lembur
                            </Label>
                            <CurrencyInput
                              id="overtime"
                              name="overtime"
                              placeholder="Uang lembur"
                              value={formik.values.overtime}
                              onChange={formik.handleChange}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label
                              for="overtimeBackup"
                              className="form-label-sm"
                            >
                              Uang Lembur Backup
                            </Label>
                            <CurrencyInput
                              id="overtimeBackup"
                              name="overtimeBackup"
                              placeholder="Uang lembur Backup"
                              value={formik.values.overtimeBackup}
                              onChange={formik.handleChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <div className="text-end">
                        <small className="text-muted">
                          Total Lembur:{" "}
                          <strong>
                            {formatCurrency(
                              Number(formik.values.overtime || 0) +
                                Number(formik.values.overtimeBackup || 0),
                            )}
                          </strong>
                        </small>
                      </div>
                    </div>

                    {/* Total Pendapatan */}
                    <div className="bg-success bg-opacity-10 p-3 rounded border border-success">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong className="text-success">
                          Total Pendapatan:
                        </strong>
                        <h5 className="text-success mb-0">
                          {formatCurrency(calculateTotalIncome())}
                        </h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* POTONGAN */}
              <Col lg={6}>
                <Card className="mb-3">
                  <CardHeader className="bg-soft-danger">
                    <h5 className="mb-0 text-danger">
                      <i className="ri-subtract-line me-2"></i>
                      Potongan
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {/* Asuransi */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h6 className="text-muted mb-0">Asuransi</h6>
                        <Button color="danger" size="sm" onClick={addInsurance}>
                          <i className="ri-add-line"></i> Tambah
                        </Button>
                      </div>
                      {insurances.map((insurance, index) => (
                        <div
                          key={index}
                          className="border rounded p-3 mb-3 bg-light"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <small className="text-muted fw-bold">
                              Asuransi #{index + 1}
                            </small>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeInsurance(index)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              checked={insurance.useCombo}
                              onChange={(e) =>
                                toggleInsuranceCombo(index, e.target.checked)
                              }
                            />
                            <Label className="form-check-label">
                              Pilih dari data asuransi
                            </Label>
                          </div>
                          {insurance.useCombo ? (
                            <Select
                              options={insuranceOptions}
                              onChange={(option: any) =>
                                handleInsuranceChange(index, option)
                              }
                              isClearable
                              placeholder="Pilih asuransi"
                              className="mb-2"
                            />
                          ) : (
                            <Input
                              type="text"
                              placeholder="Nama asuransi"
                              value={insurance.insuranceName}
                              onChange={(e) =>
                                handleInsuranceNameChange(index, e.target.value)
                              }
                              className="mb-2"
                            />
                          )}
                          <CurrencyInput
                            id="insuranceValue"
                            name="insuranceValue"
                            placeholder="Nominal asuransi"
                            value={insurance.insuranceValue}
                            onChange={(e) =>
                              handleInsuranceValueChange(index, e.target.value)
                            }
                          />
                        </div>
                      ))}
                      {insurances.length > 0 && (
                        <div className="text-end">
                          <small className="text-muted">
                            Total Asuransi:{" "}
                            <strong>
                              {formatCurrency(
                                insurances.reduce(
                                  (sum, i) =>
                                    sum + Number(i.insuranceValue || 0),
                                  0,
                                ),
                              )}
                            </strong>
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Potongan Lain */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h6 className="text-muted mb-0">Potongan Lain</h6>
                        <Button color="danger" size="sm" onClick={addDeduction}>
                          <i className="ri-add-line"></i> Tambah
                        </Button>
                      </div>
                      {deductions.map((deduction, index) => (
                        <div
                          key={index}
                          className="border rounded p-3 mb-3 bg-light"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <small className="text-muted fw-bold">
                              Potongan #{index + 1}
                            </small>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeDeduction(index)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              checked={deduction.useCombo}
                              onChange={(e) =>
                                toggleDeductionCombo(index, e.target.checked)
                              }
                            />
                            <Label className="form-check-label">
                              Pilih dari data potongan
                            </Label>
                          </div>
                          {deduction.useCombo ? (
                            <Select
                              options={deductionOptions}
                              onChange={(option: any) =>
                                handleDeductionChange(index, option)
                              }
                              isClearable
                              placeholder="Pilih potongan"
                              className="mb-2"
                            />
                          ) : (
                            <Input
                              type="text"
                              placeholder="Nama potongan"
                              value={deduction.deductionName}
                              onChange={(e) =>
                                handleDeductionNameChange(index, e.target.value)
                              }
                              className="mb-2"
                            />
                          )}
                          <CurrencyInput
                            id="deductionValue"
                            name="deductionValue"
                            placeholder="Nominal potongan"
                            value={deduction.deductionValue}
                            onChange={(e) =>
                              handleDeductionValueChange(index, e.target.value)
                            }
                          />
                        </div>
                      ))}
                      {deductions.length > 0 && (
                        <div className="text-end">
                          <small className="text-muted">
                            Total Potongan:{" "}
                            <strong>
                              {formatCurrency(
                                deductions.reduce(
                                  (sum, d) =>
                                    sum + Number(d.deductionValue || 0),
                                  0,
                                ),
                              )}
                            </strong>
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Total Potongan */}
                    <div className="bg-danger bg-opacity-10 p-3 rounded border border-danger">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong className="text-danger">Total Potongan:</strong>
                        <h5 className="text-danger mb-0">
                          {formatCurrency(calculateTotalDeductions())}
                        </h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Summary */}
            <Card className="border-primary mb-3">
              <CardBody>
                <Row className="text-center">
                  <Col md={4}>
                    <div className="p-3">
                      <small className="text-muted d-block mb-2">
                        Total Pendapatan
                      </small>
                      <h4 className="text-success mb-0">
                        {formatCurrency(calculateTotalIncome())}
                      </h4>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3">
                      <small className="text-muted d-block mb-2">
                        Total Potongan
                      </small>
                      <h4 className="text-danger mb-0">
                        {formatCurrency(calculateTotalDeductions())}
                      </h4>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 bg-primary bg-opacity-10 rounded">
                      <small className="text-muted d-block mb-2">
                        Gaji Bersih
                      </small>
                      <h3 className="text-primary mb-0 fw-bold">
                        {formatCurrency(calculateNetSalary())}
                      </h3>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardBody>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    color="light"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <i className="ri-close-line me-1"></i>
                    Batal
                  </Button>
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1"></i>
                        Simpan Slip Gaji
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddPayslip;
